// functions
import { connect, useSelector } from 'react-redux';
import * as ComponentFunctions from './ProtocolContract.Functions';
import { buildValidity, contractStatusMeta, describeValidity, paymentScheduleLabel } from './ProtocolContract.Summary';
import { useNavigate } from 'react-router-dom';
import ContractsSelectorComponent from '../../../../commons/component/Inputs/ContractSelector/ContractSelectorComponent';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import { Contract } from '../../../../models/Entities/Contracts/Contract';
import { AppState } from '../../../../redux/reducers';
import { CheckProtocolContractAssociation } from '../../../../services/Contract.Services';
import { Badge, Button, Spinner, Text } from '@fluentui/react-components';
import {
    CalendarLtrRegular,
    CheckmarkCircleRegular,
    DocumentTextRegular,
    LinkDismissRegular,
    LockClosedRegular,
    OpenRegular,
    PlugConnectedRegular,
    StarRegular,
    WarningRegular
} from '@fluentui/react-icons';

////i18Next
import { useTranslation } from 'react-i18next';

// Styles
import './ProtocolContract.Styles.css';
import { ConfirmationButtonComponent } from '../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';

const formatDate = (date: Date | string | undefined) => (date ? moment(date).format('DD/MM/YYYY') : undefined);

/** Resultado de consultar si el contrato elegido ya está atado a otro protocolo. */
type AssociationCheck =
    | { state: 'checking' }
    | { state: 'free' }
    | { state: 'linked'; protocolId: number }
    | { state: 'error' };

interface ContractCardProps {
    contract: Contract | undefined;
    isPrimary?: boolean;
    isPreview?: boolean;
    footer?: React.ReactNode;
}

/** Ficha del contrato: identidad, vigencia y cómo liquida. */
const ContractCard = ({ contract, isPrimary, isPreview, footer }: ContractCardProps) => {
    const status = contractStatusMeta(contract?.contractStatus);
    const validity = buildValidity(contract?.contractStartDate, contract?.contractEndDate);
    const schedule = paymentScheduleLabel(contract?.paymentSheduleType, contract?.paymentScheduleFrequency);

    // Sólo los datos que el contrato trae: una fila vacía no le dice nada a nadie.
    const facts: { label: string; value: string }[] = [
        { label: 'Moneda', value: contract?.contractCurrency || '' },
        { label: 'Liquidación', value: schedule || '' },
        { label: 'Primera liquidación', value: formatDate(contract?.firstSettlement) || '' },
        { label: 'Adendas', value: contract?.addendums?.length ? String(contract.addendums.length) : '' },
        { label: 'Etapas', value: contract?.stages?.length ? String(contract.stages.length) : '' },
        { label: 'Conceptos', value: contract?.concepts?.length ? String(contract.concepts.length) : '' }
    ].filter((fact) => fact.value);

    return (
        <article className={`pc-card pc-card--${status.tone}${isPreview ? ' pc-card--preview' : ''}`}>
            <header className="pc-card__head">
                <span className="pc-card__icon" aria-hidden>
                    <DocumentTextRegular />
                </span>
                <div className="pc-card__identity">
                    <span className="pc-card__eyebrow">{isPreview ? 'Contrato a asociar' : 'Contrato asociado'}</span>
                    <span className="pc-card__number" title={contract?.contractNumber}>
                        {contract?.contractNumber || `Contrato #${contract?.id ?? ''}`}
                    </span>
                </div>
                <Badge appearance="tint" color={status.tone} className="pc-card__status">
                    {status.label}
                </Badge>
            </header>

            {contract?.contractDescription && (
                <p className="pc-card__description" title={contract.contractDescription}>
                    {contract.contractDescription}
                </p>
            )}

            {(isPrimary || contract?.contractVerified || contract?.isLocked) && (
                <div className="pc-card__tags">
                    {isPrimary && (
                        <Badge appearance="outline" color="brand" icon={<StarRegular />}>
                            Principal
                        </Badge>
                    )}
                    {contract?.contractVerified && (
                        <Badge appearance="outline" color="success" icon={<CheckmarkCircleRegular />}>
                            Verificado
                        </Badge>
                    )}
                    {contract?.isLocked && (
                        <Badge appearance="outline" color="subtle" icon={<LockClosedRegular />}>
                            Bloqueado
                        </Badge>
                    )}
                </div>
            )}

            <section className={`pc-validity pc-validity--${validity.state}`}>
                <div className="pc-validity__head">
                    <CalendarLtrRegular className="pc-validity__icon" />
                    <span className="pc-validity__title">Vigencia</span>
                    <span className="pc-validity__note">{describeValidity(validity)}</span>
                </div>
                <div className="pc-validity__dates">
                    <span>{formatDate(contract?.contractStartDate) || 'Sin inicio'}</span>
                    <span className="pc-validity__arrow" aria-hidden>→</span>
                    <span>{formatDate(contract?.contractEndDate) || 'Sin fin'}</span>
                </div>
                {validity.percent !== undefined && (
                    <div
                        className="pc-validity__bar"
                        role="progressbar"
                        aria-label="Vigencia transcurrida"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={validity.percent}
                    >
                        <span style={{ width: `${validity.percent}%` }} />
                    </div>
                )}
            </section>

            {facts.length > 0 && (
                <dl className="pc-facts">
                    {facts.map((fact) => (
                        <div key={fact.label} className="pc-facts__row">
                            <dt>{fact.label}</dt>
                            <dd>{fact.value}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {footer && <footer className="pc-card__footer">{footer}</footer>}
        </article>
    );
};

export const ProtocolContractComponent = (props: ComponentFunctions.Props) => {

    const navigate = useNavigate();
    const [t] = useTranslation();
    const accessToken = useSelector((state: AppState) => (state as any)?.oidc?.user?.access_token as string | undefined);

    const [selectedContractToAssociate, setSelectedContractToAssociate] = useState<Contract | undefined>(undefined);
    const [check, setCheck] = useState<AssociationCheck | undefined>(undefined);

    // effects
    useEffect(() => {
        props.Protocol && props.GetAllProtocolContractAssociations(props.ProtocolId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.ProtocolId, props.Protocol !== undefined]);

    useEffect(() => {
        props.Protocol?.contractAssociation?.contractId && props.GetContractById(props.Protocol?.contractAssociation.contractId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.Protocol?.contractAssociation?.contractId]);

    // Cada contrato elegido se verifica contra la API: un contrato que ya
    // liquida otro protocolo no se puede asociar acá.
    useEffect(() => {
        const contractId = selectedContractToAssociate?.id;
        if (contractId === undefined || !accessToken) {
            setCheck(undefined);
            return;
        }

        let cancelled = false;
        setCheck({ state: 'checking' });
        CheckProtocolContractAssociation(contractId, accessToken)
            .then((protocolId) => {
                if (cancelled) return;
                setCheck(
                    protocolId === null || protocolId === undefined || protocolId === props.ProtocolId
                        ? { state: 'free' }
                        : { state: 'linked', protocolId }
                );
            })
            .catch(() => !cancelled && setCheck({ state: 'error' }));

        return () => {
            cancelled = true;
        };
    }, [selectedContractToAssociate?.id, accessToken, props.ProtocolId]);

    const protocolName = (protocolId: number) => {
        const protocol = props.Protocols?.find((item: Protocol) => item.id === protocolId);
        return protocol?.title || protocol?.fullTitle || `#${protocolId}`;
    };

    if (!props.Protocol) {
        return (
            <div className="pc-container">
                <div className="pc-loading">
                    <Spinner size="small" label={t('protocolContract.onVerification.loading')} labelPosition="after" />
                </div>
            </div>
        );
    }

    const association = props.Protocol.contractAssociation;

    // ── Con contrato ──
    if (association?.contractId) {
        return (
            <div className="pc-container">
                {props.Contract ? (
                    <ContractCard
                        contract={props.Contract}
                        isPrimary={association.isPrimary}
                        footer={
                            <>
                                <Button
                                    appearance="primary"
                                    icon={<OpenRegular />}
                                    onClick={() => navigate(`/contracts/${association.contractId}`)}
                                >
                                    Ver contrato
                                </Button>
                                <ConfirmationButtonComponent
                                    ButtonId={'disassociateContract'}
                                    ButtonIconName={'RemoveLink'}
                                    ButtonText={t('protocolContract.buttons.disassociate')}
                                    ButtonCssClass={'pc-disassociate'}
                                    ConfirmationButtonText={t('protocolContract.buttons.disassociate')}
                                    ConfirmationButtonCssClass={'danger'}
                                    CancelButtonText={t('protocolContract.buttons.cancelButton')}
                                    CancelButtonCssClass={''}
                                    Title={t('protocolContract.buttons.disassociateContract')}
                                    Description={`Va a desasociar el contrato ${props.Contract.contractNumber} del protocolo ${props.Protocol.fullTitle}`}
                                    ElementTextHeader={props.Contract.contractNumber || ''}
                                    ElementTextInformation={t('protocolContract.buttons.information')}
                                    isOperationExecuting={false}
                                    isOperationExecutedWithError={false}
                                    OperationError={undefined}
                                    onConfirmation={() => props.DisassociateContractFromProtocol(association)}
                                    onCancel={() => {}}
                                />
                            </>
                        }
                    />
                ) : (
                    <div className="pc-loading">
                        <Spinner size="small" label="Cargando contrato..." labelPosition="after" />
                    </div>
                )}
            </div>
        );
    }

    // ── Sin contrato ──
    const canAssociate = check?.state === 'free';

    return (
        <div className="pc-container">
            <div className="pc-empty">
                <span className="pc-empty__icon" aria-hidden>
                    <LinkDismissRegular />
                </span>
                <Text weight="semibold" className="pc-empty__title">
                    {t('protocolContract.noContract.unassociatedProtocol')}
                </Text>
                <Text size={200} className="pc-empty__hint">
                    Sin contrato no hay precios para valorizar los consumos ni liquidaciones. Buscá el contrato y asocialo.
                </Text>
            </div>

            <div className="pc-picker">
                <span className="pc-picker__label">Buscar contrato</span>
                <ContractsSelectorComponent
                    onSelectedContract={(selectedContracts: Contract[]) => setSelectedContractToAssociate(selectedContracts[0])}
                    ContractsSelected={selectedContractToAssociate ? [selectedContractToAssociate.id] : []}
                    MultiselectEnable={false}
                />
            </div>

            {selectedContractToAssociate && (
                <ContractCard
                    contract={selectedContractToAssociate}
                    isPreview
                    footer={
                        <>
                            {check?.state === 'checking' && (
                                <span className="pc-check">
                                    <Spinner size="tiny" /> {t('protocolContract.onVerification.verifying')}
                                </span>
                            )}
                            {check?.state === 'free' && (
                                <span className="pc-check pc-check--ok">
                                    <CheckmarkCircleRegular /> {t('protocolContract.onVerification.verificationOk')}
                                </span>
                            )}
                            {check?.state === 'linked' && (
                                <span className="pc-check pc-check--warn">
                                    <WarningRegular /> Ya está asociado al protocolo {protocolName(check.protocolId)}.
                                </span>
                            )}
                            {check?.state === 'error' && (
                                <span className="pc-check pc-check--warn">
                                    <WarningRegular /> No se pudo verificar el contrato. Probá elegirlo de nuevo.
                                </span>
                            )}
                            <Button
                                appearance="primary"
                                icon={<PlugConnectedRegular />}
                                disabled={!canAssociate}
                                onClick={() => props.AssociateContractToProtocol(selectedContractToAssociate.id, props.Protocol.id)}
                            >
                                {t('protocolContract.buttons.associateContract')}
                            </Button>
                        </>
                    }
                />
            )}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ProtocolContractComponent as any);
